import React, { useEffect } from "react";

// Material UI
import {
  Alert,
  Switch,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

// Styles
import { textStyles, commonStyles } from "Common/Styles";

// Components
import { CardInfo, FormikTextField } from "Common/Components";
import TruncateText from "Common/TruncateText";

// Form Fields
import { generalInfoFields, clientAppointmentOptions } from "../Common/fields";

const getColumns = (formik) => [
  {
    field: "label",
    headerName: "Features",
    flex: 0.6,
  },

  {
    field: "description",
    headerName: "Description",
    flex: 1,
    renderCell: (params) => (
      <TruncateText
        name="description"
        id="description"
        text={params.row.description}
      />
    ),
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.3,
    renderCell: (params) => (
      <Switch
        name={`settings.${params.row.key}`}
        id={`settings.${params.row.key}`}
        checked={formik.values.settings?.[params.row.key]}
        onChange={() => {
          const currentValue = formik.values.settings?.[params.row.key];
          formik.setFieldValue(`settings.${params.row.key}`, !currentValue);
        }}
        sx={commonStyles.terracottaSwitch}
      />
    ),
  },
];

const GeneralInfoPage = ({ formik, viewOnly = false }) => {
  // On mount, set showInformationFields to true if information exists and it's currently false
  useEffect(() => {
    const locations = formik.values.appointmentLocations || [];
    let updated = false;
    const newLocations = locations.map((loc) => {
      const hasInfo = Object.values(loc.information || {}).some((val) => !!val);
      if (hasInfo && !loc.showInformationFields) {
        updated = true;
        return { ...loc, showInformationFields: true };
      }
      return loc;
    });
    if (updated) {
      formik.setFieldValue("appointmentLocations", newLocations);
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <CardInfo
        title="General Information"
        tooltipInfo="General information about the client"
      >
        {generalInfoFields.map((field) => (
          <FormikTextField
            key={field.id}
            formik={formik}
            field={{
              ...field,
              ...(viewOnly && { disabled: true, review: true }),
            }}
            name={field.id}
          />
        ))}
      </CardInfo>

      {/* Settings Table */}
      {!viewOnly && (
        <CardInfo
          title="Settings"
          tooltipInfo="At least one setting must be enabled to create the client."
        >
          {formik.touched.settings && formik.errors.settings && (
            <Alert severity="error">{formik.errors.settings}</Alert>
          )}
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {getColumns(formik).map((col) => (
                    <TableCell key={col.field} sx={textStyles.subtitle}>
                      {col.headerName || col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!clientAppointmentOptions.length ? (
                  <TableRow>
                    <TableCell colSpan={getColumns(formik).length}>
                      <Typography>No features available.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  clientAppointmentOptions.map((row, idx) => (
                    <TableRow key={row.id || idx}>
                      {getColumns(formik).map((col) => (
                        <TableCell key={col.field} sx={textStyles.infoText}>
                          {col.renderCell
                            ? col.renderCell({ row })
                            : row[col.field]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardInfo>
      )}
    </>
  );
};

export default GeneralInfoPage;
